// Lager 1 — read-only admin booking detail panel.
//
// Opens when a non-subscription, non-league_match, non-linked-open-match
// booking is clicked in AdminScheduleGrid. Shows summary + booker info +
// full player list + payment status. NO mutating actions in Lager 1.
//
// Routing precedence (handled in AdminScheduleGrid.routeBookingClick):
//   1. linked_league_match → MatchDetailModal       (unchanged)
//   2. linked_open_match   → ActivityDetailModal    (unchanged)
//   3. else (plain private, or legacy is_open_match without canonical link)
//      → THIS panel
//
// D10 (shell-track): adopts the shared BookingDetailShell — visual unity with
// SubscriptionDetailDialog (operator's reference look). Sheet → Dialog
// container switch is operator-locked (Fas 1 §3 §4a §8 B-default).
//
// Polish-fix bundled with the shell adoption: the previous render produced
// an empty <li/> when invitedPlayers.length === 0 and there were no
// open-match empty seats. Now we skip that placeholder entirely so the
// player list shows only real rows.
//
// All display derivation flows through `bookingDetailDisplay.ts` helpers
// (canonical-helper pattern) — no per-view conditionals here.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
// Lager 2.2: Tooltip-imports borttagna — "Återbetala enligt regler"-knapp
// är nu aktiv i shell-footer (Decision 10.2). UI-transparens (belopp+tier)
// renderas inom dialogens radio-sub-text (Decision 10.4) istället för
// disabled-tooltip-pattern.
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/utils/formatPrice";
import {
  getBookingType,
  derivePaymentStatus,
  resolvePlayerDisplay,
  deriveOpenMatchSeats,
  type PaymentStatusInfo,
  type PlayerRowDisplay,
} from "@/lib/bookingDetailDisplay";
import { BookingDetailShell } from "./BookingDetailShell";
import { BookingRefundCancelDialog } from "./BookingRefundCancelDialog";
import type { ScheduleBooking } from "./types";
import type { Tables } from "@/integrations/supabase/types";

interface BookingDetailPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: ScheduleBooking | null;
  /** Lager 3a — when set, the panel renders a "Flytta tid"-button in the
   *  shell footer. On click the callback fires with the current booking;
   *  the parent (AdminScheduleGrid) is expected to close the panel and
   *  enter click-to-move mode on the grid. Omit to hide the button (e.g.
   *  if the panel is mounted in a context without grid click-to-move). */
  onMoveBooking?: (booking: ScheduleBooking) => void;
}

// Booker profile + customer (with category) — for the booker-name row.
interface BookerInfo {
  userId: string;
  fullName: string | null;
  phone: string | null;
  categorySlug: string | null;
  categoryName: string | null;
}

// Invited / joined player row — for the player list.
interface PlayerInfo {
  userId: string | null;
  fullName: string | null;
  categorySlug: string | null;
  categoryName: string | null;
}

// PaymentRow-interfacet borttaget i Lager 2.1 — query nyckar nu direkt
// mot Tables<"booking_payments">[] (samma shape som dialogen behöver).
// derivePaymentStatus tar PaymentLikeRow vilket är strukturellt
// kompatibelt med Tables<>-shapen.

export function BookingDetailPanel({ open, onOpenChange, booking, onMoveBooking }: BookingDetailPanelProps) {
  const { t } = useTranslation("admin");

  // ── Booker query (customer with category ONLY) ──
  // Profile name + phone come pre-embedded on ScheduleBooking via the grid
  // query (bookings JOIN profiles). We do NOT direct-query profiles here:
  // the `profiles` RLS policies are self-read + parent-child-link only — no
  // admin-org extension — so direct-fetch would return empty for any booker
  // who isn't the admin themselves. The embedded version routes through
  // `bookings` RLS (admin can read bookings in their org → profile embed
  // satisfies the JOIN), so name/phone are reliably available.
  //
  // Customer (with category) IS org-scoped via `get_user_org_ids()` —
  // RLS-verified. Cross-org bookings cannot leak category data through this query.
  const { data: bookerInfo } = useQuery<BookerInfo | null>({
    queryKey: ["booking-detail-panel-booker", booking?.user_id],
    queryFn: async () => {
      if (!booking?.user_id) return null;
      const { data: customer } = await supabase
        .from("customers")
        .select("id, customer_categories(slug, name)")
        .eq("user_id", booking.user_id)
        .maybeSingle();
      const cat = (customer as { customer_categories: { slug: string; name: string } | null } | null)
        ?.customer_categories;
      return {
        userId: booking.user_id,
        fullName: booking.profiles?.full_name ?? null,
        phone: booking.profiles?.phone ?? null,
        categorySlug: cat?.slug ?? null,
        categoryName: cat?.name ?? null,
      };
    },
    enabled: open && !!booking?.user_id,
  });

  // ── Players query (booking_players JOIN profiles + customers) ──
  // Lesson 18 — exhaustive source: ALL joined players (open-match) or all
  // invited players (private). Empty open-match seats handled by deriveOpenMatchSeats.
  const { data: invitedPlayers = [] } = useQuery<PlayerInfo[]>({
    queryKey: ["booking-detail-panel-players", booking?.id],
    queryFn: async () => {
      if (!booking?.id) return [];
      const { data, error } = await supabase
        .from("booking_players")
        .select(
          "user_id, profiles:booking_players_user_id_fkey(full_name)",
        )
        .eq("booking_id", booking.id);
      if (error) return [];
      const rows = (data ?? []) as Array<{
        user_id: string | null;
        profiles: { full_name: string | null } | null;
      }>;
      const userIds = rows.map((r) => r.user_id).filter((v): v is string => !!v);
      const categories = new Map<string, { slug: string; name: string }>();
      if (userIds.length > 0) {
        const { data: custs } = await supabase
          .from("customers")
          .select("user_id, customer_categories(slug, name)")
          .in("user_id", userIds);
        for (const c of (custs ?? []) as Array<{
          user_id: string;
          customer_categories: { slug: string; name: string } | null;
        }>) {
          if (c.customer_categories) categories.set(c.user_id, c.customer_categories);
        }
      }
      return rows.map((r) => {
        const cat = r.user_id ? categories.get(r.user_id) ?? null : null;
        return {
          userId: r.user_id,
          fullName: r.profiles?.full_name ?? null,
          categorySlug: cat?.slug ?? null,
          categoryName: cat?.name ?? null,
        };
      });
    },
    enabled: open && !!booking?.id,
  });

  // ── Payment query (booking_payments aggregate) ──
  // Lesson 16 — booking_payments is canonical truth for payment state.
  // Lager 2.1: extended SELECT to include id + stripe_payment_intent_id
  // + user_id så BookingRefundCancelDialog (D9-modul) kan executera
  // refund-flow utan parallell fetch (canonical-helper).
  const { data: payments = [] } = useQuery<Tables<"booking_payments">[]>({
    queryKey: ["booking-detail-panel-payments", booking?.id],
    queryFn: async () => {
      if (!booking?.id) return [];
      const { data, error } = await supabase
        .from("booking_payments")
        .select("*")
        .eq("booking_id", booking.id);
      if (error) return [];
      return (data ?? []) as Tables<"booking_payments">[];
    },
    enabled: open && !!booking?.id,
  });

  // Lager 2.1 — refund/cancel-dialog state. Initialiseras via D2-knappar
  // i shell-footer; mode-toggle inom dialog (radio-buttons) hanterar
  // regression-coverage av AdminCancelBookingDialog (Decision 10.3).
  const [refundCancelInitialMode, setRefundCancelInitialMode] = useState<
    "full_override" | "cancel_no_refund" | null
  >(null);
  const refundCancelOpen = refundCancelInitialMode !== null;

  // Härleda om bokningen har betalda payments (för knapp-state)
  const hasPaidPayments = payments.some((p) =>
    ["paid", "manual"].includes(p.status),
  );

  // Lager 2.1 — bokar-email för cancellation-email (Decision 10.6).
  // Samma pattern som BookingDetailModal: customers.email via customer_id
  // ELLER user_id-lookup.
  const { data: bookerEmail } = useQuery<string | null>({
    queryKey: ["booking-detail-panel-booker-email", booking?.user_id, (booking as { customer_id?: string } | null)?.customer_id],
    queryFn: async () => {
      if (!booking) return null;
      const customerId = (booking as { customer_id?: string | null }).customer_id;
      if (customerId) {
        const { data: cust } = await supabase
          .from("customers")
          .select("email")
          .eq("id", customerId)
          .maybeSingle();
        if (cust?.email) return cust.email;
      }
      if (booking.user_id) {
        const { data } = await supabase
          .from("customers")
          .select("email")
          .eq("user_id", booking.user_id)
          .maybeSingle();
        return data?.email ?? null;
      }
      return null;
    },
    enabled: open && !!booking?.user_id,
  });

  // Empty-booking guard — shell still renders header so user has something
  // to dismiss even if no booking selected (e.g. transient state).
  if (!booking) {
    return (
      <BookingDetailShell
        open={open}
        onOpenChange={onOpenChange}
        title={t("bookingDetailPanel.title")}
        subtitle={t("bookingDetailPanel.loading")}
      >
        <BookingDetailPanelLoading />
      </BookingDetailShell>
    );
  }

  const bookingType = getBookingType({
    status: booking.status,
    is_open_match: booking.is_open_match,
  });
  const paymentInfo = derivePaymentStatus(
    { price_total: booking.price_total, price_total_minor: booking.price_total_minor },
    payments,
  );

  // Booker as a player row (always first, isBooker=true)
  const bookerRow: PlayerRowDisplay = resolvePlayerDisplay({
    userId: bookerInfo?.userId ?? booking.user_id,
    fullName: bookerInfo?.fullName ?? booking.profiles?.full_name ?? null,
    categorySlug: bookerInfo?.categorySlug ?? null,
    categoryName: bookerInfo?.categoryName ?? null,
    isBooker: true,
  });

  // Invited / joined rows
  const invitedRows: PlayerRowDisplay[] = invitedPlayers
    .filter((p) => p.userId !== booking.user_id) // don't double-list booker
    .map((p) =>
      resolvePlayerDisplay({
        userId: p.userId,
        fullName: p.fullName,
        categorySlug: p.categorySlug,
        categoryName: p.categoryName,
        isBooker: false,
      }),
    );

  // Open-match seat derivation — actual seat count is from court.max_players
  const isOpenMatch = bookingType === "open_match";
  const seatInfo =
    isOpenMatch && typeof booking.max_players === "number"
      ? deriveOpenMatchSeats(booking.max_players, invitedRows.length + 1)
      : null;

  const startDate = new Date(booking.start_time);
  const endDate = new Date(booking.end_time);
  const dateLabel = format(startDate, "EEEE d MMMM yyyy", { locale: sv });
  const timeLabel = `${format(startDate, "HH:mm")} – ${format(endDate, "HH:mm")}`;
  const courtLabel = booking.courts?.name ?? "—";
  const durationLabel = t("bookingDetailPanel.duration_minutes", {
    minutes: booking.duration_minutes ?? 0,
  });

  // Lager 3a — only show "Flytta tid" for non-cancelled non-blocked private
  // bookings. Open-match / blocked / cancelled don't fit the simple
  // same-price click-to-move flow (open-match has its own reschedule path
  // via ActivityDetailModal; blocked/cancelled aren't reschedulable here).
  const canShowMoveButton =
    !!onMoveBooking && (bookingType === "private" || bookingType === "open_match")
      && booking.status !== "cancelled"
      && booking.status !== "blocked";

  // Lager 2.1 — D2-knappar för refund/cancel (sibling till "Flytta tid").
  // Visa endast för cancel:able bokningar (= ej redan cancelled/blocked).
  // F19.8: båda alltid synliga (även när inga betalningar — knappen
  // auto-degraderar till cancel-no-refund-läge via resolvern).
  const canCancel =
    booking.status !== "cancelled" && booking.status !== "blocked";
  const moveButton = canShowMoveButton ? (
    <Button
      size="sm"
      variant="outline"
      onClick={() => onMoveBooking && onMoveBooking(booking)}
      data-testid="booking-detail-panel-move-time"
    >
      {t("bookingDetailPanel.moveTime")}
    </Button>
  ) : null;
  const refundCancelButtons = canCancel ? (
    <>
      <Button
        size="sm"
        variant="outline"
        className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
        onClick={() =>
          setRefundCancelInitialMode(
            hasPaidPayments ? "full_override" : "cancel_no_refund",
          )
        }
        data-testid="booking-detail-panel-full-refund"
      >
        {t("bookingDetailPanel.fullRefund", {
          defaultValue: "Full återbetalning",
        })}
      </Button>
      {/* Lager 2.2: "Återbetala enligt regler"-knapp AKTIVERAD (var
          disabled-med-tooltip i 2.1). Decision 10.2: aktiv även för demo
          (tiers=null) — dialogen visar då "Inga regler konfigurerade —
          full återbetalning ges". P1-tier-resolvern (Alt A —
          org.settings.cancellation.tiers) körs i dialogen vid open. */}
      <Button
        size="sm"
        variant="outline"
        className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
        onClick={() => setRefundCancelInitialMode("per_rules")}
        data-testid="booking-detail-panel-per-rules-refund"
      >
        {t("bookingDetailPanel.refundPerRules", {
          defaultValue: "Återbetala enligt regler",
        })}
      </Button>
    </>
  ) : null;

  const footer =
    moveButton || refundCancelButtons ? (
      <div className="flex gap-2 flex-wrap">
        {moveButton}
        {refundCancelButtons}
      </div>
    ) : null;

  return (
    <BookingDetailShell
      open={open}
      onOpenChange={onOpenChange}
      title={t("bookingDetailPanel.title")}
      subtitle={
        <>
          {dateLabel} · {timeLabel} · {courtLabel}
        </>
      }
      headerBadge={
        <Badge variant="secondary" data-testid="booking-type-badge">
          {t(`bookingDetailPanel.type.${bookingType}`)}
        </Badge>
      }
      footer={footer}
    >
      {/* ── Summary (2-col grid per SubscriptionDetailDialog canonical look) ── */}
      <section data-testid="section-summary">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground text-xs">
              {t("bookingDetailPanel.field.date")}
            </span>
            <p className="font-medium">{dateLabel}</p>
          </div>
          <div>
            <span className="text-muted-foreground text-xs">
              {t("bookingDetailPanel.field.time")}
            </span>
            <p className="font-medium">{timeLabel}</p>
          </div>
          <div>
            <span className="text-muted-foreground text-xs">
              {t("bookingDetailPanel.field.court")}
            </span>
            <p className="font-medium">{courtLabel}</p>
          </div>
          <div>
            <span className="text-muted-foreground text-xs">
              {t("bookingDetailPanel.field.duration")}
            </span>
            <p className="font-medium">{durationLabel}</p>
          </div>
        </div>
      </section>

      {/* ── Booker ── */}
      <section data-testid="section-booker">
        <span className="text-muted-foreground text-xs">
          {t("bookingDetailPanel.section.booker")}
        </span>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{bookerRow.displayName}</span>
          {bookerRow.categoryLabel && (
            <Badge variant="outline" className="text-[10px] py-0">
              {bookerRow.categoryLabel}
            </Badge>
          )}
        </div>
        {bookerInfo?.phone && (
          <div className="text-xs text-muted-foreground">
            {t("bookingDetailPanel.field.phone")}: {bookerInfo.phone}
          </div>
        )}
      </section>

      {/* ── Players ── */}
      <section data-testid="section-players">
        <span className="text-muted-foreground text-xs">
          {t("bookingDetailPanel.section.players")}
        </span>
        <ul className="space-y-1 text-sm mt-1">
          {/* Booker row */}
          <li key={bookerRow.key} className="flex items-center gap-2">
            <span>{bookerRow.displayName}</span>
            <span className="text-xs text-muted-foreground">
              {t("bookingDetailPanel.player.booker")}
            </span>
            {bookerRow.categoryLabel && (
              <Badge variant="outline" className="text-[10px] py-0">
                {bookerRow.categoryLabel}
              </Badge>
            )}
          </li>
          {/* Invited / joined rows */}
          {invitedRows.map((row) => (
            <li key={row.key} className="flex items-center gap-2">
              <span>{row.displayName}</span>
              {row.categoryLabel && (
                <Badge variant="outline" className="text-[10px] py-0">
                  {row.categoryLabel}
                </Badge>
              )}
            </li>
          ))}
          {/* Open-match empty seats (only when isOpenMatch + capacity known) */}
          {seatInfo && seatInfo.emptySeats > 0 && (
            <>
              {Array.from({ length: seatInfo.emptySeats }).map((_, i) => (
                <li
                  key={`__empty_${i}`}
                  className="flex items-center gap-2 text-muted-foreground italic"
                >
                  <span>· {t("bookingDetailPanel.player.emptyOpenSeat")}</span>
                </li>
              ))}
              <li className="text-xs text-muted-foreground pt-1">
                {t("bookingDetailPanel.player.emptyOpenSeatsHint", {
                  count: seatInfo.emptySeats,
                })}
              </li>
            </>
          )}
          {/* D10 polish-fix: no empty-row placeholder when private + 0 invited.
              The booker row above is sufficient; rendering an empty <li/> here
              was a Lager 1 oversight (returned an empty list item without text). */}
        </ul>
      </section>

      {/* ── Payment ── */}
      <section data-testid="section-payment">
        <span className="text-muted-foreground text-xs">
          {t("bookingDetailPanel.section.payment")}
        </span>
        <div className="mt-1">
          <PaymentSummary info={paymentInfo} />
        </div>
      </section>

      {/* Lager 2.1 — Shared refund/cancel-dialog mounted här så
          state-management förblir lokal till panelen (state-pattern
          analog till Lager 3a:s click-to-move). */}
      {booking && (
        <BookingRefundCancelDialog
          open={refundCancelOpen}
          onOpenChange={(v) => {
            if (!v) setRefundCancelInitialMode(null);
          }}
          kind={
            // Legacy open-match går genom samma "private"-path eftersom
            // payments är aggregerade (D6-deltagar-loop = Lager 2.3).
            bookingType === "private" || bookingType === "open_match"
              ? "private"
              : "private"
          }
          initialMode={refundCancelInitialMode}
          booking={booking}
          payments={payments}
          bookerEmail={bookerEmail ?? null}
          playerCount={invitedPlayers.length + 1}
          onCancelled={() => {
            setRefundCancelInitialMode(null);
            onOpenChange(false);
          }}
        />
      )}
    </BookingDetailShell>
  );
}

// ─── Sub-component: payment summary (single derivation, no inline status checks)

function PaymentSummary({ info }: { info: PaymentStatusInfo }) {
  const { t } = useTranslation("admin");
  const totalKr = info.totalAmountMinor / 100;
  const paidKr = info.paidAmountMinor / 100;
  const totalLabel = formatPrice(totalKr);
  const paidLabel = formatPrice(paidKr);

  switch (info.kind) {
    case "free":
      return <p className="text-sm">{t("bookingDetailPanel.payment.free")}</p>;
    case "paid":
      return <p className="text-sm">{t("bookingDetailPanel.payment.paid", { amount: totalLabel })}</p>;
    case "pending":
      return (
        <p className="text-sm text-amber-700">
          {t("bookingDetailPanel.payment.pending", { amount: totalLabel })}
        </p>
      );
    case "partial":
      return (
        <p className="text-sm text-orange-700">
          {t("bookingDetailPanel.payment.partial", { paid: paidLabel, total: totalLabel })}
        </p>
      );
    case "refunded":
      return <p className="text-sm text-muted-foreground">{t("bookingDetailPanel.payment.refunded")}</p>;
    case "unknown":
    default:
      return (
        <p className="text-sm text-muted-foreground">
          {t("bookingDetailPanel.payment.unknown")} · {totalLabel}
        </p>
      );
  }
}

// Re-export for default-import compatibility in lazy imports if needed
export default BookingDetailPanel;

// Skeleton placeholder is exported for tests / loading states elsewhere
export function BookingDetailPanelLoading() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-6 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}
